import { useState, useEffect, useCallback, RefObject } from 'react';

export interface SelectionSearchData {
  text: string;
  usageCount: number;
  position: { x: number; y: number };
  isVisible: boolean;
}

interface UseTextSelectionSearchOptions {
  usageIndex: Map<string, Set<string>>;
  minLength?: number;
  debounceMs?: number;
  containerRef?: RefObject<HTMLElement>;
  zIndexBase?: number;
}

interface UseTextSelectionSearchReturn extends SelectionSearchData {
  handleSearch: () => void;
  handleDismiss: () => void;
}

/**
 * Hook to detect text selection and provide search functionality with usage count
 * Supports both regular page content and modal containers
 */
export const useTextSelectionSearch = ({
  usageIndex,
  minLength = 3,
  debounceMs = 50,
  containerRef,
  zIndexBase = 10000
}: UseTextSelectionSearchOptions): UseTextSelectionSearchReturn => {
  const [selectionData, setSelectionData] = useState<SelectionSearchData>({
    text: '',
    usageCount: 0,
    position: { x: 0, y: 0 },
    isVisible: false
  });

  // Calculate position for floating button
  const calculatePosition = useCallback((selectionRect: DOMRect): { x: number; y: number } => {
    // If we're in a modal container, calculate position relative to it
    if (containerRef?.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const scrollTop = containerRef.current.scrollTop || 0;

      // Calculate position relative to container
      const x = selectionRect.left + selectionRect.width / 2;
      const y = selectionRect.top - containerRect.top + scrollTop - 50;

      return { x, y };
    }

    // Regular page positioning
    const yOffset = selectionRect.top < 80 ? selectionRect.bottom + 10 : selectionRect.top - 50;

    // Center horizontally, but keep on screen
    let xPosition = selectionRect.left + selectionRect.width / 2;
    const buttonWidth = 400; // Max button width

    if (xPosition + buttonWidth / 2 > window.innerWidth) {
      xPosition = window.innerWidth - buttonWidth - 20;
    } else if (xPosition - buttonWidth / 2 < 0) {
      xPosition = buttonWidth / 2 + 20;
    }

    return { x: xPosition, y: yOffset };
  }, [containerRef]);

  // Handle selection change
  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      setSelectionData(prev => ({ ...prev, isVisible: false }));
      return;
    }

    let text = selection.toString().trim();

    // Remove line numbers if present (common in code blocks)
    text = text.replace(/^\d+\s+/gm, '');
    // Remove extra whitespace from multi-line selections
    text = text.replace(/\s+/g, ' ').trim();

    // Clean up function call syntax for matching
    // Try both with and without parentheses - first try exact match, then without ()
    const originalText = text;
    // Remove all empty parentheses (so "GetGame().IsMultiplayer()" becomes "GetGame.IsMultiplayer")
    text = text.replace(/\(\)/g, '');
    // Remove trailing brackets if present
    text = text.replace(/\[\]$/g, '');

    // Check if text meets minimum length requirement
    if (text.length < minLength) {
      setSelectionData(prev => ({ ...prev, isVisible: false }));
      return;
    }

    // Get selection position
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();

    // Calculate button position
    const position = calculatePosition(rect);

    // Look up usage count in index - try multiple variations
    let usageSet = usageIndex.get(text);

    // If not found with cleaned text, try with original text (for cases like "GetGame().IsMultiplayer()")
    if (!usageSet && originalText !== text) {
      usageSet = usageIndex.get(originalText);
    }

    // If still not found and text contains dots, try splitting and looking up last method
    // e.g., "GetGame.IsMultiplayer" → try "IsMultiplayer"
    if (!usageSet && text.includes('.')) {
      const parts = text.split('.');
      const lastPart = parts[parts.length - 1];
      usageSet = usageIndex.get(lastPart);
    }

    const usageCount = usageSet ? usageSet.size : 0;

    // Debug logging
    console.log(`[Selection] Original: "${originalText}", Cleaned: "${text}", Count: ${usageCount}, Classes:`, usageSet ? Array.from(usageSet) : 'not found');

    setSelectionData({
      text: originalText, // Use original text for display and search
      usageCount,
      position,
      isVisible: true
    });
  }, [minLength, usageIndex, calculatePosition]);

  // Debounced selection handler
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const debouncedHandler = () => {
      timeoutId = setTimeout(handleSelectionChange, debounceMs);
    };

    document.addEventListener('selectionchange', debouncedHandler);

    return () => {
      document.removeEventListener('selectionchange', debouncedHandler);
      clearTimeout(timeoutId);
    };
  }, [handleSelectionChange, debounceMs]);

  // Handle click outside to dismiss
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Don't dismiss if clicking on the floating button itself
      const target = e.target as HTMLElement;
      if (target.closest('[data-floating-search-button]')) {
        return;
      }
      setSelectionData(prev => ({ ...prev, isVisible: false }));
    };

    if (selectionData.isVisible) {
      // Add small delay to prevent immediate dismissal
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [selectionData.isVisible]);

  // Handle Escape key to dismiss
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectionData(prev => ({ ...prev, isVisible: false }));
        // Clear selection
        window.getSelection()?.removeAllRanges();
      }
    };

    if (selectionData.isVisible) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [selectionData.isVisible]);

  const handleSearch = useCallback(() => {
    // This will be provided by the parent component
    // For now, just dismiss the button
    setSelectionData(prev => ({ ...prev, isVisible: false }));
  }, []);

  const handleDismiss = useCallback(() => {
    setSelectionData(prev => ({ ...prev, isVisible: false }));
    window.getSelection()?.removeAllRanges();
  }, []);

  return {
    ...selectionData,
    handleSearch,
    handleDismiss
  };
};
